import {
  Activity,
  BarChart4,
  FileBarChart2,
  Home,
  LayoutPanelLeft,
  type LucideProps,
  MessageCircle,
  Network,
  Sparkles,
  UserRoundCog,
  Zap,
} from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';

export type WorkspaceSidebarLeafItem = {
  kind: 'leaf';
  key: string;
  label: string;
  href: string;
  IconComponent?: ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>
  >;
};

export type WorkspaceSidebarGroupItem = {
  kind: 'group';
  key: string;
  label: string;
  href?: string;
  hideChevron?: boolean;
  IconComponent?: ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>
  >;
  children: WorkspaceSidebarLeafItem[];
};

export type WorkspaceSidebarItem =
  | WorkspaceSidebarLeafItem
  | WorkspaceSidebarGroupItem;

export type WorkspaceSidebarSection = {
  key: string;
  label?: string;
  items: WorkspaceSidebarItem[];
};

export type WorkspaceSidebarSchema = {
  showSearch: boolean;
  groups: WorkspaceSidebarSection[];
};

export type WorkspaceSidebarSchemaOptions = {
  isEditor?: boolean;
  isFaceCream?: boolean;
  isProdMode?: boolean;
  hideCustomer?: boolean;
};

const defaultSchemaOptions: Required<WorkspaceSidebarSchemaOptions> = {
  isEditor: true,
  isFaceCream: false,
  isProdMode: true,
  hideCustomer: false,
};

const homeItem = (): WorkspaceSidebarLeafItem => ({
  kind: 'leaf',
  key: 'home',
  label: 'Home',
  href: '/home',
  IconComponent: Home,
});

const heySynclyGroup = (): WorkspaceSidebarLeafItem[] => [
  {
    kind: 'leaf',
    key: 'hey-syncly',
    label: 'Hey Syncly',
    href: '/hey-syncly',
    IconComponent: Sparkles,
  },
  {
    kind: 'leaf',
    key: 'hey-syncly-reports',
    label: 'Reports',
    href: '/reports',
    IconComponent: FileBarChart2,
  },
];

const managementGroup = (
  props: {
    hideCustomer: boolean;
  } = { hideCustomer: false },
): WorkspaceSidebarGroupItem => {
  const children: WorkspaceSidebarLeafItem[] = [
    {
      kind: 'leaf',
      key: 'contacts',
      label: 'Contacts',
      href: '/customers',
    },
    {
      kind: 'leaf',
      key: 'accounts',
      label: 'Accounts',
      href: '/companies',
    },
    {
      kind: 'leaf',
      key: 'agents',
      label: 'Agents',
      href: '/agents',
    },
  ];

  return {
    kind: 'group',
    key: 'managements',
    label: 'Managements',
    IconComponent: UserRoundCog,
    children: children.filter(
      (item) => !props.hideCustomer || item.key !== 'contacts',
    ),
  };
};

const filterItems = (items: Array<WorkspaceSidebarItem | false | null>) =>
  items.filter(
    (item): item is WorkspaceSidebarItem => item !== false && item !== null,
  );

const buildLegacySchema = (
  options: Required<WorkspaceSidebarSchemaOptions>,
): WorkspaceSidebarSchema => {
  return {
    showSearch: true,
    groups: [
      {
        key: 'legacy-primary',
        items: [homeItem(), ...heySynclyGroup()],
      },
      {
        key: 'legacy-explore',
        label: 'Explore',
        items: filterItems([
          {
            kind: 'leaf',
            key: 'feedback',
            label: 'Feedback',
            href: '/feedbacks',
            IconComponent: MessageCircle,
          },
          {
            kind: 'leaf',
            key: 'insight-explorer',
            label: 'Insight Explorer',
            href: '/insight-explorer',
            IconComponent: BarChart4,
          },
          {
            kind: 'leaf',
            key: 'taxonomy',
            label: 'Taxonomy',
            href: '/taxonomy',
            IconComponent: Network,
          },
        ]),
      },
      {
        key: 'legacy-monitor',
        label: 'Monitor',
        items: filterItems([
          {
            kind: 'leaf',
            key: 'signals',
            label: 'Signals',
            href: '/signals',
            IconComponent: Activity,
          },
          {
            kind: 'leaf',
            key: 'dashboard',
            label: 'Dashboard',
            href: '/dashboard2/list',
            IconComponent: LayoutPanelLeft,
          },
          {
            kind: 'leaf',
            key: 'workflows',
            label: 'Workflows',
            href: '/workflow',
            IconComponent: Zap,
          },
        ]),
      },
      ...(options.isEditor
        ? [
            {
              key: 'legacy-management',
              items: [managementGroup({ hideCustomer: options.hideCustomer })],
            },
          ]
        : []),
    ],
  };
};

export const getWorkspaceSidebarSchema = (
  options: WorkspaceSidebarSchemaOptions = {},
): WorkspaceSidebarSchema => {
  const resolvedOptions = { ...defaultSchemaOptions, ...options };

  return buildLegacySchema(resolvedOptions);
};

export const getWorkspaceNavigatorLeafItems = (
  options: WorkspaceSidebarSchemaOptions = {},
): WorkspaceSidebarLeafItem[] =>
  getWorkspaceSidebarSchema(options).groups.flatMap((group) =>
    group.items.flatMap((item) => {
      if (item.kind === 'leaf') {
        return [item];
      }

      const groupLeaf = item.href
        ? [
            {
              kind: 'leaf' as const,
              key: item.key,
              label: item.label,
              href: item.href,
              IconComponent: item.IconComponent,
            },
          ]
        : [];

      return [...groupLeaf, ...item.children];
    }),
  );

export const getDefaultWorkspaceHref = (
  options: WorkspaceSidebarSchemaOptions = {},
): string =>
  getWorkspaceNavigatorLeafItems(options)[0]?.href ?? '/home';
